import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';

export default function VoidedOrders() {
  const { user ,companyId} = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };
  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

const loadPlan = async () => {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
  }
};

  useEffect(() => {
    loadPlan();
    // Set today's date as default for both from and to date
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const loadCompanyName = async () => {
    if (!user) return;
    const snap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
    if (snap.exists()) setCompanyName(snap.data().companyName || 'Company');
  };

  const loadVoidedOrders = async () => {
    if (!user) return;
    const q = query(
      collection(db, 'companies', companyId, 'orders'),
      where('paymentStatus', '==', 'Void'),
      orderBy('voidedAt', 'desc') 
    );
    const snapshot = await getDocs(q);
    const voided = snapshot.docs.map(doc => doc.data()).filter(order => {
      const date = order.voidedAt?.toDate?.() || new Date(order.voidedAt);
      const from = fromDate ? new Date(fromDate) : new Date(0);
      const to = toDate ? new Date(toDate) : new Date();
      to.setHours(23, 59, 59, 999);
      return date >= from && date <= to;
    });
    setOrders(voided);
  };

  const filtered = orders.filter(o =>
    String(o.receiptNumber || '').toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const totalLost = filtered.reduce((sum, o) => sum + (o.total || 0), 0);

  const exportToPDF = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }
    const doc = new jsPDF();
    const title = `Voided Orders Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;

    doc.setFontSize(12);
    doc.text(companyName, 105, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.text(title, 105, 22, { align: 'center' });

    autoTable(doc, {
      head: [['Voided At', 'Void Reason', 'Receipt No', 'Total']],
      body: filtered.map(o => [
        (o.voidedAt?.toDate?.() || new Date(o.voidedAt)).toLocaleString(),
        o.voidReason || '-',
        '#' + String(o.receiptNumber).padStart(6, '0'),
        (o.total || 0).toFixed(2)
      ]),
      startY: 30,
      styles: { fontSize: 9 },
    });

    doc.text(`Total Lost Revenue: RM ${totalLost.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 10);

    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(9);
    doc.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });

    const file = `voided-orders_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`.replace(/\//g, '-');
    doc.save(file);
  };

    useEffect(() => {
      const loadAll = async () => {
        setLoading(true);
        await Promise.all([loadVoidedOrders(), loadCompanyName()]);
        setLoading(false);
      };
      loadAll();
    }, [user, fromDate, toDate]);
  
    if (loading) {
      return (
        <div className="w-full flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
          <span className="text-white">Loading information...</span>
        </div>
      );
    }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-2">Voided Orders</h2>

      <div className="flex gap-4 mb-6 flex-wrap">
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <span className="text-gray-400">to</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <input type="text" placeholder="Search by receipt number" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <button onClick={exportToPDF} className="bg-purple-600 px-4 py-2 rounded">Export to PDF</button>
      </div>

      <div className="text-lg font-semibold text-white mb-6">
        Total Lost: <span className="text-red-500">RM {totalLost.toFixed(2)}</span>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
        <thead>
  <tr className="text-left text-gray-400">
    <th className="px-3 py-2 w-32">Voided At</th>
    <th className="px-4 py-2 w-2/5">Void Reason</th>
    <th className="px-4 py-2">Receipt #</th>
    <th className="px-4 py-2">Total</th>
  </tr>
</thead>
<tbody>
  {paginated.map((item, i) => (
    <tr key={i} className="border-t border-gray-700">
      <td className="px-4 py-2">
        {formatDate(item.voidedAt) + ' ' + formatTime(item.voidedAt)}
      </td>
      <td className="px-4 py-2 text-gray-300 whitespace-pre-line">
        {item.voidReason || '-'}
      </td>
      <td className="px-4 py-2">
        {'#' + (item.receiptNumber)}
      </td>
      <td className="px-4 py-2">RM {item.total?.toFixed(2)}</td>
    </tr>
  ))}
  {paginated.length === 0 && (
    <tr>
      <td className="px-4 py-6 text-center text-gray-400" colSpan={3}>
        No voided orders found.
      </td>
    </tr>
  )}
</tbody>
        </table>
      </div>

      <div className="flex justify-center mt-6 gap-2">
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded ${currentPage === i + 1 ? 'bg-purple-600 text-white' : 'bg-[#2c2c2c] text-gray-300'}`}
            onClick={() => setCurrentPage(i + 1)}
          >{i + 1}</button>
        ))}
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}
